import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useOutsideClick,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { DatePicker, Link, Spinner } from "@src/components/ui-kit";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { now } from "mobx-utils";
import { Fragment, useRef } from "react";
import {
  Entry,
  Form,
  Group,
  PopoverActivator,
  StartSaveButton,
  StopButton,
} from "./components";
import { useFetchTimeTrackingItems } from "./hooks";

export const TimeTrackingPopover = observer(function TimeTrackingPopover() {
  const {
    timeTrackingPopoverStore: store,
    timeTrackingStore,
    workspaceStore,
  } = useStore();
  const wrapperRef = useRef(null);
  const loading = useFetchTimeTrackingItems(store);
  const { isMobile } = useScreenType();

  const onClose = () => {
    store.togglePopover(false);
    store.form.reset();
  };

  const handleCancel = () => {
    if (store.form.editMode) {
      store.form.reset();
      return;
    }

    onClose();
  };

  useOutsideClick({
    ref: wrapperRef,
    handler() {
      onClose();
    },
  });

  return (
    <Box ref={wrapperRef}>
      <Popover
        closeOnBlur={isMobile ? false : true}
        isLazy
        isOpen={store.popoverActive}
        onOpen={() => {
          store.form.tracked_for_date.onChange(new Date(now()));
          store.refetchEntries();
        }}
        placement="bottom-end"
      >
        <PopoverTrigger>
          <Box onClick={() => store.togglePopover()}>
            <PopoverActivator />
          </Box>
        </PopoverTrigger>

        <Portal containerRef={wrapperRef}>
          <PopoverContent
            ref={store.popoverRef}
            overflowY="scroll"
            w="min-content"
            minW={{
              base: "100vw",
              md: "550px",
              lg: "600px",
            }}
            maxH="calc(100vh - 55px)"
          >
            <PopoverHeader p="4">
              <HStack align="baseline" justify="space-between">
                <Heading size="xs">
                  <Trans>Time Tracking</Trans>
                </Heading>
                <Link href={{ pathname: "/time-tracking/day" }}>
                  <Trans>My Timesheet</Trans>
                </Link>
              </HStack>
            </PopoverHeader>

            <PopoverBody p="0">
              <Box p="4">
                {timeTrackingStore.runningEntry ? (
                  <Entry running value={timeTrackingStore.runningEntry} />
                ) : (
                  <Fragment>
                    <Form
                      value={store.form}
                      isReadOnly={store.form.is_locked}
                    />
                    {!store.form.editMode && (
                      <Flex justify="end" w="full">
                        <DatePicker
                          trigger={
                            <Button variant="link">
                              <Trans>Track for:</Trans>
                              &nbsp;
                              <Text>
                                {store.form.tracked_for_date.$.toLocaleDateString(
                                  workspaceStore.settings?.locale,
                                )}
                              </Text>
                            </Button>
                          }
                          selected={store.form.tracked_for_date.$}
                          onChange={(range) => {
                            if (!range?.start) return;

                            store.form.tracked_for_date.onChange(range.start);
                            store.form.task_id.reset();
                            store.form.budget_item_id.reset();

                            runInAction(() => {
                              store.form.selectedTask = undefined;
                            });
                          }}
                        />
                      </Flex>
                    )}
                  </Fragment>
                )}

                {timeTrackingStore.timerRunningForTooLong && (
                  <Text color="red.500">{store.longRunningText}</Text>
                )}

                <HStack justify="end" my="4" spacing="3">
                  {loading && (
                    <div>
                      <Spinner size="sm" />
                    </div>
                  )}
                  <Button
                    colorScheme="grey"
                    onClick={handleCancel}
                    variant="outline"
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  {timeTrackingStore.runningEntry ? (
                    <StopButton
                      timeTrackingItem={timeTrackingStore.runningEntry}
                      onStop={() => {
                        timeTrackingStore.updateTrackedTimeInListing(
                          store.form.task_id.$,
                        );
                        store.refetchEntries();
                        store.popoverRef.current?.focus();
                      }}
                    />
                  ) : (
                    <StartSaveButton
                      form={store.form}
                      onSave={() => {
                        timeTrackingStore.updateTrackedTimeInListing(
                          store.form.task_id.$,
                        );
                        store.form.reset();
                        store.refetchEntries();
                        store.togglePopover(false);
                      }}
                      onStart={() => {
                        store.form.reset();
                        store.refetchEntries();
                        store.togglePopover(false);
                        timeTrackingStore.refetch?.();
                      }}
                    />
                  )}
                </HStack>
              </Box>

              {!timeTrackingStore.runningEntry &&
                store.groupedEntries.map(([group, entries]) => (
                  <Group key={group} label={new Date(group)} value={entries} />
                ))}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
});
